import { ChangeDetectionStrategy, Component, effect, inject, signal } from '@angular/core';
import {AppState} from '../store';
import {Store} from '@ngrx/store';
import {selectGoogleAnalyticsConfig, selectUserPermissions} from '../store/selectors';
import {filter} from 'rxjs/operators';
import {NavigationEnd, Router} from '@angular/router';
import {GoogleAnalyticsConfig} from '../models/google-analytics-config.model';
import {UserPermissions} from '../models/user-permissions.model';
import {environment} from '../../environments/environment';
import {checkIfPlatformSite} from '../common/platform-utils';
import {getGoogleTrackingIdKind} from '../common/google-tracking-id';
import { runAfterUserInteraction, runWhenIdle } from '../common/idle-utils';
import { toSignal } from '@angular/core/rxjs-interop';

interface GoogleAnalyticsData {
  config: GoogleAnalyticsConfig;
  permissions: UserPermissions;
}

@Component({
    selector: "google-analytics-integration",
    template: "",
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoogleAnalyticsIntegrationComponent{
  private store = inject<Store<AppState>>(Store);
  private router = inject(Router);


  private readonly initialized = signal(false);

  gaTrackingId: string;

  // Which loader we ended up using. Page views are reported differently for each, so we have to
  // remember the decision rather than re-derive it on every navigation.
  private trackingMode: 'gtm' | 'gtag' | null = null;

  private readonly config = toSignal(this.store.select(selectGoogleAnalyticsConfig));
  private readonly permissions = toSignal(this.store.select(selectUserPermissions));

  private readonly navigationEnd = toSignal(
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd)
    )
  );

  constructor() {
    effect(() => {
      const config = this.config();
      const permissions = this.permissions();

      if (permissions && !this.initialized()) {
        this.activateGoogleAnalyticsIntegration({config, permissions});
      }
    });

    effect(() => {
      const nav = this.navigationEnd();
      if (this.initialized() && nav) {
        runWhenIdle(() => this.reportPageView());
      }
    });
  }
  
  activateGoogleAnalyticsIntegration({config, permissions}: GoogleAnalyticsData) {  
    // if the user is an admin, use the platform tracking Id
    if (permissions.isAdmin || checkIfPlatformSite()) {
      this.gaTrackingId = environment?.google?.googleAnalyticsTrackingId;
      console.log(`Using platform Google Analytics Tracking Id ${this.gaTrackingId}`);
    }
    // otherwise, use the creator tracking Id, if one is available
    else if (config?.gaTrackingId && config.enabled) {
      this.gaTrackingId = config.gaTrackingId;
      console.log(`Using course creator Google Analytics Tracking Id ${this.gaTrackingId}`);
    }

    if (this.gaTrackingId) {
      runAfterUserInteraction(() => this.loadGoogleAnalyticsScript(this.gaTrackingId));
      this.initialized.set(true);
    }

  }

  /**
   *
   * Google hands out several different ids, and they do NOT share a loader script:
   *
   *   GTM-XXXXXXX  Tag Manager container   ->  googletagmanager.com/gtm.js
   *   G-XXXXXXXXXX GA4 measurement id      ->  googletagmanager.com/gtag/js
   *   AW-XXXXXXXXX Google Ads conversion   ->  googletagmanager.com/gtag/js
   *   DC-XXXXXXXX  Floodlight              ->  googletagmanager.com/gtag/js
   *   UA-XXXXXX-Y  Universal Analytics     ->  googletagmanager.com/gtag/js (property is retired,
   *                                            but gtag is still the right loader for it)
   *
   * School Settings has a single "tracking Id" field, and until now we sent whatever was typed
   * there to gtm.js. For every id that is not a GTM- container that is the wrong script, which is
   * what Google's "deploys Google tag ... via a Google Tag Manager snippet (gtm.js)" email is
   * reporting. It comes from here, not from the snippet the school pasted into Google's console.
   *
   */
  loadGoogleAnalyticsScript(gaTrackingId: string) {

    const trackingId = (gaTrackingId ?? '').trim();
    const kind = getGoogleTrackingIdKind(trackingId);

    if (kind === 'gtm') {
      this.trackingMode = 'gtm';
      this.loadTagManagerScript(trackingId);
    }
    else if (kind === 'gtag') {
      this.trackingMode = 'gtag';
      this.loadGtagScript(trackingId);
    }
    else {
      // Not a shape Google recognises. Loading it anyway would just 404 against Google and, for
      // the schools that pasted an entire snippet or a phone number into the field, put junk in a
      // script URL. Better to do nothing and say why.
      this.trackingMode = null;
      console.warn(`Google tracking Id "${trackingId}" is not a recognised Google id ` +
        `(expected GTM-, G-, AW-, DC- or UA-), so no tracking script was loaded.`);
    }
  }

  /**
   * A school can end up with the same tag twice: once here in the Google Analytics integration,
   * and once pasted by hand under School Settings / Third Party Scripts. 49 schools currently do
   * exactly that, and it makes Google count every visit twice.
   *
   * So before adding a tag we check whether it is already on the page. If it is we leave it
   * alone - but we still keep trackingMode set, because their pasted snippet only reports the
   * page it loaded on and cannot see this app's route changes. They end up with one correct tag
   * plus the page views the snippet was missing.
   */
  private isGoogleTagAlreadyOnPage(trackingId: string): boolean {

    // the loader script itself, whether it has finished loading or not
    const scriptPresent = !!document.querySelector(
      `script[src*="googletagmanager.com"][src*="${trackingId}"]`);

    // or an inline snippet that already ran gtag('config', <id>)
    const dataLayer = (window as any)?.dataLayer;
    const alreadyConfigured = Array.isArray(dataLayer) &&
      dataLayer.some((entry: any) => entry && entry[0] === 'config' && entry[1] === trackingId);

    return scriptPresent || alreadyConfigured;
  }

  /** Tag Manager container - unchanged, this is the path that was always correct. */
  private loadTagManagerScript(trackingId: string) {

    if (this.isGoogleTagAlreadyOnPage(trackingId)) {
      console.log(`Google Tag Manager container ${trackingId} is already on this page, not adding it again.`);
      return;
    }

    // Begin Google Tag Manager
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j:any=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer',trackingId);
    // End Google Tag Manager

  }

  /**
   * GA4 / Google Ads / Floodlight. This is the snippet Google documents and the one the schools
   * are given in their own Google console, reproduced exactly.
   */
  private loadGtagScript(trackingId: string) {

    const w = window as any;

    // dataLayer and gtag() have to exist before the library loads - gtag queues the calls and the
    // library drains the queue once it arrives. gtag() must forward its `arguments` object, so it
    // cannot be an arrow function.
    w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== 'function') {
      w.gtag = function () { w.dataLayer.push(arguments); };
    }

    if (this.isGoogleTagAlreadyOnPage(trackingId)) {
      console.log(`Google tag ${trackingId} is already on this page, not adding it again.`);
      return;
    }

    w.gtag('js', new Date());
    w.gtag('config', trackingId);

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(trackingId)}`;
    document.head.appendChild(script);
  }

  reportPageView() {

    if (this.trackingMode === 'gtag') {
      // gtag('config') only counts the page the tag loaded on. This is a single page app, so every
      // later route change has to be reported by hand or the school sees one page view per visit.
      const gtagFn = (window as any)?.gtag;

      if (typeof gtagFn === 'function') {
        const pageView = {
          page_title: document.title,
          page_location: window?.location?.toString(),
          page_path: location.pathname
        };
        console.log(`Reporting page_view to Google tag ${this.gaTrackingId}`, pageView);
        gtagFn('event', 'page_view', pageView);
      }
      return;
    }

    if (this.trackingMode === 'gtm') {
      // report the page view to Google Tag Manager
      window['dataLayer'] = window['dataLayer'] || [];

      const gtmEvent = {
        'event': 'virtualPageview',
        'pageUrl': window?.location?.toString(),
        'pageTitle': document.title
      }
      console.log(`Reporting GTM event to GTM container ${this.gaTrackingId}`, gtmEvent);

      window['dataLayer'].push(gtmEvent);
    }

    // Nothing to do when no valid id was configured - loadGoogleAnalyticsScript already warned.
  }

}
